import { mfsError } from '../../errors';
import { isTooLong, throwIfEmptyValue, throwIfEmptyValues, throwIfTooLong } from '../../errors/utility';
import { MAX_RELATED_ITEMS_PER_OP } from '../mfsDataConstraints';
import { MfsItemId, MfsRelationship } from '../mfsTypes';

export const tooManyRelatedItemsError = new Error(
  `Max ${MAX_RELATED_ITEMS_PER_OP} relatedItems are allowed per operation.`
);

type RelatedItemsBatchFn = (itemId: MfsItemId, relationship: MfsRelationship, relatedItemIds: MfsItemId[]) => void;
type RelatedItemsFn = (itemId: MfsItemId, relationship: MfsRelationship, ...relatedItemIds: MfsItemId[]) => void;

/** A method decorator that validates the specified relatedItems method. */
export function validateRelatedItemsBatchOperation(
  _target: unknown,
  _input: string,
  descriptor: TypedPropertyDescriptor<RelatedItemsBatchFn>
): TypedPropertyDescriptor<RelatedItemsBatchFn> {
  const originalMethod = descriptor.value;

  if (!originalMethod) {
    return descriptor;
  }

  descriptor.value = function (itemId, relationship, relatedItemIds) {
    validateOperation(itemId, relationship, relatedItemIds);
    return originalMethod.apply(this, [itemId, relationship, relatedItemIds]);
  };

  return descriptor;
}

/** Validates the given relatedItems method. */
export function validateRelatedItemsOperation(
  _target: unknown,
  _input: string,
  descriptor: TypedPropertyDescriptor<RelatedItemsFn>
): TypedPropertyDescriptor<RelatedItemsFn> {
  const originalMethod = descriptor.value;

  if (!originalMethod) {
    return descriptor;
  }

  descriptor.value = function (itemId, relationship, ...relatedItemIds) {
    validateOperation(itemId, relationship, relatedItemIds);
    return originalMethod.apply(this, [itemId, relationship, ...relatedItemIds]);
  };

  return descriptor;
}

function validateOperation(itemId: string, relationship: MfsRelationship, relatedItemIds: string[]) {
  throwIfTooLong('itemId', itemId);
  throwIfEmptyValue('itemId', itemId);
  throwIfEmptyValues('relatedItemId', relatedItemIds);
  throwIfEmptyValue('relationship.type', relationship.type);
  throwIfEmptyValue('relationship.direction', relationship.direction);
  throwIfTooManyItems(relatedItemIds);
  relatedItemIds.some((relatedItemId) => {
    if (isTooLong(relatedItemId)) {
      throw mfsError.maxInputLengthExceeded('relatedItemId');
    }
  });
}

/**
 * Ensures that the given array of `values` are within size limits that allow for atomic execution.
 *
 * @param uuidValues The array of uniform length string (uuid) values to validate.
 */
export function throwIfTooManyItems(uuidValues: string[]): void {
  // Ops involving the add/removal of the given uuidValues are going to be sent atomically using ContainerRuntime.OrderSequentially
  // which requires that total size of all messages must be less than maxOpSize.
  // This limit is Delta connection dependent, though commonly set as 16K (JSON serialized size).
  // A single UUID value is 36 bytes & there's additional data into the op + JSON serialization doubles the size.
  // If the relationship is two-way that's another factor. To be on the safe side, we add a hard limit on the num items right now.
  if (uuidValues.length > MAX_RELATED_ITEMS_PER_OP) {
    throw tooManyRelatedItemsError;
  }
}
